/**
 * `Assets_Executor.ts` — port of Unity's `Executor/Assets_Executor.cs` for
 * the web-registered asset tools (materials + delete model).
 */

import type { IProjectModel } from "@models/project/IProjectModel";
import type { IMaterialModel } from "@models/materials/IMaterialModel";
import type { ISelectionModel } from "@models/scene/ISelectionModel";
import { AgentToolNames } from "@ai/registry/agentToolNames";
import {
  buildPathIndex,
  displayNameOf,
  normalizePath,
  resolveManyByPath,
  rootPathOf,
  type PathEntry,
} from "@ai/context/hierarchyPaths";
import {
  getString,
  getStringList,
  type IDomainExecutor,
  type ToolArgs,
} from "./AgentToolExecutorModel";

export class AssetsExecutor implements IDomainExecutor {
  private readonly m_project: IProjectModel;
  private readonly m_materials: IMaterialModel;
  private readonly m_selection: ISelectionModel;

  constructor(project: IProjectModel, materials: IMaterialModel, selection: ISelectionModel) {
    this.m_project = project;
    this.m_materials = materials;
    this.m_selection = selection;
  }

  async tryExecute(toolName: string, args: ToolArgs): Promise<string | null> {
    switch (toolName) {
      case AgentToolNames.GetPartMaterials:
        return this.getPartMaterials(args);
      case AgentToolNames.GetMaterialLibrary:
        return this.getMaterialLibrary(args);
      case AgentToolNames.ApplyMaterialOnParts:
        return this.applyMaterialOnParts(args);
      case AgentToolNames.DeleteModel:
        return this.deleteModel(args);
      default:
        return null;
    }
  }

  private get sceneId(): string | null {
    return this.m_project.activeScene?.id ?? null;
  }

  private async getPartMaterials(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    const path = getString(args, "partPath");
    if (!path) return "error: Get_Part_Materials_missing_partPath";

    const entities = this.m_project.getEntities(sceneId);
    const entry = buildPathIndex(entities).find((e) => e.path === normalizePath(path));
    if (!entry) return `error: part not found at path '${path}'`;

    const previews = this.m_materials.getEntityMaterials(sceneId, entry.snapshot.id);
    if (previews.length === 0) {
      return `ok: no editable materials on name:${displayNameOf(entry.snapshot)} path:${entry.path}`;
    }
    const lines = previews.map(
      (m) =>
        `materialId:"${m.id}" name:"${m.displayName}" shader:${m.shaderFamily ?? m.shader} baseColor:${m.previewColor}`,
    );
    return lines.join("\n");
  }

  private async getMaterialLibrary(args: ToolArgs): Promise<string> {
    const prompt = getString(args, "prompt")?.toLowerCase() ?? null;
    const groups = this.m_materials.getLibraryGroups();
    if (groups.length === 0) return "error: material library is empty";

    const lines: string[] = [];
    for (const group of groups) {
      const materials = prompt
        ? group.materials.filter(
            (m) =>
              m.displayName.toLowerCase().includes(prompt) ||
              group.name.toLowerCase().includes(prompt),
          )
        : group.materials;
      if (materials.length === 0) continue;
      lines.push(`${group.name}:`);
      for (const material of materials) {
        lines.push(`  "${material.displayName}" (baseColor:${material.previewColor})`);
      }
    }
    if (lines.length === 0) {
      return `ok: no library materials matched '${prompt}'. Call again without a prompt for the full list.`;
    }
    return lines.join("\n");
  }

  private async applyMaterialOnParts(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    const materialId = getString(args, "materialId");
    if (!materialId) return "error: apply_material_missing_materialId";

    const library = this.m_materials
      .getLibraryGroups()
      .flatMap((g) => g.materials)
      .find(
        (m) =>
          m.displayName.toLowerCase() === materialId.toLowerCase() ||
          m.id.toLowerCase() === materialId.toLowerCase(),
      );
    if (!library) {
      return `error: material '${materialId}' not found in the library. Use Get_Material_Library first.`;
    }

    const paths = getStringList(args, "partPaths");
    const entities = this.m_project.getEntities(sceneId);
    let entries: PathEntry[] = resolveManyByPath(entities, paths);
    if (entries.length === 0 && this.m_selection.selectedIds.size > 0) {
      entries = buildPathIndex(entities).filter((e) =>
        this.m_selection.isSelected(e.snapshot.id),
      );
    }
    if (entries.length === 0) {
      return "error: no target parts. Provide partPaths resolved via Get_Scene_Context.";
    }

    for (const entry of entries) {
      await this.m_materials.applyLibraryMaterialToSnapshot(
        sceneId,
        entry.snapshot.id,
        library.id,
      );
    }
    const names = entries.map((e) => `'${displayNameOf(e.snapshot)}'`).join(", ");
    return `ok: applied material '${library.displayName}' to ${entries.length} part(s): ${names}`;
  }

  private async deleteModel(args: ToolArgs): Promise<string> {
    const sceneId = this.sceneId;
    if (!sceneId) return "error: no active scene";
    const entities = this.m_project.getEntities(sceneId);
    const index = buildPathIndex(entities);

    const paths = [
      ...getStringList(args, "modelPaths"),
      ...(getString(args, "modelPath") ? [getString(args, "modelPath")!] : []),
    ];
    let roots: PathEntry[] = [];
    if (paths.length > 0) {
      const rootPaths = new Set(paths.map((p) => rootPathOf(p)));
      roots = index.filter((e) => e.isRoot && rootPaths.has(e.path));
    } else {
      const name = getString(args, "modelName");
      if (name) {
        const matches = index.filter(
          (e) => e.isRoot && displayNameOf(e.snapshot).toLowerCase() === name.toLowerCase(),
        );
        if (matches.length > 1) {
          return `error: model name '${name}' matched ${matches.length} models. Resolve with Get_Scene_Context and pass modelPaths.`;
        }
        roots = matches;
      } else if (this.m_selection.selectedIds.size > 0) {
        const selectedRootPaths = new Set(
          index
            .filter((e) => this.m_selection.isSelected(e.snapshot.id))
            .map((e) => rootPathOf(e.path)),
        );
        roots = index.filter((e) => e.isRoot && selectedRootPaths.has(e.path));
      }
    }

    if (roots.length === 0) {
      return "error: no valid root entities (models) found to delete. Resolve targets with Get_Scene_Context first.";
    }

    this.m_selection.clear();
    const names: string[] = [];
    for (const root of roots) {
      names.push(`'${displayNameOf(root.snapshot)}'`);
      await this.m_project.deleteEntityAsync(sceneId, root.entity.id);
    }
    return `ok: deleted ${roots.length} model(s): ${names.join(", ")}`;
  }
}
